import React, { useState, useEffect, useCallback } from 'react';
import { FaExclamationTriangle, FaCheck, FaTimes } from 'react-icons/fa';
import conflictResolver, { ConflictStrategy } from '../../offline/ConflictResolver';
import offlineStorage from '../../offline/OfflineStorage';
import syncEngine from '../../offline/SyncEngine';
import { showToast } from '../../utils/toast';

const SyncConflictModal = () => {
  const [conflicts, setConflicts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const unsub = conflictResolver.onConflict((pendingConflicts) => {
      setConflicts(pendingConflicts);
      if (pendingConflicts.length > 0 && currentIndex >= pendingConflicts.length) {
        setCurrentIndex(0);
      }
    });
    return unsub;
  }, [currentIndex]);

  const currentConflict = conflicts[currentIndex];

  const handleResolve = useCallback(async (strategy) => {
    if (!currentConflict || resolving) return;
    setResolving(true);

    try {
      const resolvedData = await conflictResolver.resolve(currentConflict, strategy);

      if (resolvedData) {
        const { localRecord } = currentConflict;
        await offlineStorage.save(localRecord.entityType, resolvedData);
        conflictResolver.dismissConflict(localRecord.localId);
        showToast('Conflict resolved automatically', { type: 'success', duration: 3000 });
      } else {
        conflictResolver.dismissConflict(currentConflict.localRecord.localId);
        showToast('Conflict skipped', { type: 'info', duration: 3000 });
      }

      if (currentIndex >= conflicts.length - 1) {
        setConflicts(prev => prev.filter(c => c.localRecord?.localId !== currentConflict.localRecord?.localId));
      }
    } catch (err) {
      showToast('Failed to resolve conflict', { type: 'error', duration: 5000 });
    } finally {
      setResolving(false);
    }
  }, [currentConflict, currentIndex, conflicts.length, resolving]);

  const handleDismiss = useCallback(() => {
    if (!currentConflict) return;
    conflictResolver.dismissConflict(currentConflict.localRecord.localId);
    showToast('Conflict skipped', { type: 'info', duration: 3000 });
  }, [currentConflict]);

  const handleUseLocal = () => handleResolve(ConflictStrategy.CLIENT_WINS);
  const handleUseServer = () => handleResolve(ConflictStrategy.SERVER_WINS);
  const handleMerge = () => handleResolve(ConflictStrategy.MERGE_FIELDS);

  if (!currentConflict || conflicts.length === 0) return null;

  const { localRecord, serverData, localVersion, serverVersion } = currentConflict;

  return (
    <div className="sync-conflict-overlay">
      <div className="sync-conflict-modal">
        <div className="sync-conflict-header">
          <FaExclamationTriangle className="sync-conflict-warning-icon" />
          <h3>Sync Conflict Detected</h3>
          <span className="sync-conflict-count">{currentIndex + 1} of {conflicts.length}</span>
        </div>

        <div className="sync-conflict-body">
          <p className="sync-conflict-description">
            This record was modified both locally and on the server.
            Choose how to resolve the conflict.
          </p>

          <div className="sync-conflict-versions">
            <div className="sync-conflict-version sync-conflict-version--local">
              <h4>Your Version (v{localVersion})</h4>
              <pre className="sync-conflict-data">{JSON.stringify(localRecord?.data || {}, null, 2)}</pre>
            </div>
            <div className="sync-conflict-version sync-conflict-version--server">
              <h4>Server Version (v{serverVersion})</h4>
              <pre className="sync-conflict-data">{JSON.stringify(serverData || {}, null, 2)}</pre>
            </div>
          </div>
        </div>

        <div className="sync-conflict-actions">
          <button className="sync-conflict-btn sync-conflict-btn--local" onClick={handleUseLocal} disabled={resolving}>
            <FaCheck /> Keep Mine
          </button>
          <button className="sync-conflict-btn sync-conflict-btn--server" onClick={handleUseServer} disabled={resolving}>
            <FaCheck /> Use Server
          </button>
          <button className="sync-conflict-btn sync-conflict-btn--merge" onClick={handleMerge} disabled={resolving}>
            <FaCheck /> Auto Merge
          </button>
          <button className="sync-conflict-btn sync-conflict-btn--dismiss" onClick={handleDismiss} disabled={resolving}>
            <FaTimes /> Skip
          </button>
        </div>

        {resolving && <div className="sync-conflict-resolving">Resolving...</div>}

        <style>{`
          .sync-conflict-overlay {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5); z-index: 50000;
            display: flex; align-items: center; justify-content: center;
          }
          .sync-conflict-modal {
            background: #fff; border-radius: 12px; max-width: 700px; width: 90%;
            max-height: 85vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          }
          .sync-conflict-header {
            display: flex; align-items: center; gap: 10px;
            padding: 20px 24px; border-bottom: 1px solid #e5e7eb;
            background: #fffbeb;
          }
          .sync-conflict-warning-icon { color: #f59e0b; font-size: 1.2rem; }
          .sync-conflict-header h3 { margin: 0; flex: 1; font-size: 1.1rem; }
          .sync-conflict-count { font-size: 0.85rem; color: #6b7280; }
          .sync-conflict-body { padding: 20px 24px; }
          .sync-conflict-description { color: #4b5563; margin: 0 0 16px; font-size: 0.9rem; }
          .sync-conflict-versions { display: flex; gap: 12px; }
          .sync-conflict-version { flex: 1; }
          .sync-conflict-version h4 { margin: 0 0 8px; font-size: 0.9rem; color: #374151; }
          .sync-conflict-data {
            background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px;
            padding: 10px; font-size: 0.75rem; max-height: 200px; overflow-y: auto;
            white-space: pre-wrap; word-break: break-all;
          }
          .sync-conflict-version--local .sync-conflict-data { border-color: #3b82f6; }
          .sync-conflict-version--server .sync-conflict-data { border-color: #10b981; }
          .sync-conflict-actions {
            display: flex; gap: 10px; padding: 16px 24px;
            border-top: 1px solid #e5e7eb; flex-wrap: wrap;
          }
          .sync-conflict-btn {
            display: flex; align-items: center; gap: 6px; padding: 8px 16px;
            border: 1px solid #d1d5db; border-radius: 6px; background: #fff;
            cursor: pointer; font-size: 0.85rem; transition: all 0.15s;
          }
          .sync-conflict-btn:hover:not(:disabled) { background: #f3f4f6; }
          .sync-conflict-btn:disabled { opacity: 0.5; cursor: not-allowed; }
          .sync-conflict-btn--local { border-color: #3b82f6; color: #2563eb; }
          .sync-conflict-btn--server { border-color: #10b981; color: #059669; }
          .sync-conflict-btn--merge { border-color: #8b5cf6; color: #7c3aed; }
          .sync-conflict-btn--dismiss { color: #6b7280; }
          .sync-conflict-resolving {
            text-align: center; padding: 8px 24px 16px;
            color: #6b7280; font-size: 0.85rem;
          }
          @media (max-width: 600px) {
            .sync-conflict-versions { flex-direction: column; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default SyncConflictModal;
